import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import logoUrl from "@/assets/logo.svg";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { mapApiError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

interface OrgOption {
  id: string;
  name: string;
  slug: string;
}

type Step = "password" | "email" | "code" | "org-select";
type LoginPath = "password" | "magic-link";

export function LoginPage() {
  const { t } = useTranslation();
  const { user, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?next= carries the viewer URL (cross-origin); ?returnTo= is for in-app routes.
  const rawNext = searchParams.get("next") ?? "";
  // Restrict ?next= to: relative paths, the known viewer origin (VITE_API_URL),
  // or the current admin-console origin. Rejects arbitrary http/https URLs to
  // prevent open redirect (RAISE-18837 R1; full HMAC signing deferred to RAISE-18840).
  //
  // Dev fallback: when VITE_API_URL is not set (empty), the viewer runs on
  // localhost at a different port. Accept any localhost URL so the post-login
  // redirect works in dev without disabling the security check in production
  // (in production VITE_API_URL is always set, so this branch never activates).
  const viewerOrigin = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";
  const isLocalhostUrl = (url: string): boolean => {
    try {
      const { hostname } = new URL(url);
      return hostname === "localhost" || hostname === "127.0.0.1";
    } catch {
      return false;
    }
  };
  // Guard against protocol-relative paths like //evil.com and /\evil.com that
  // browsers treat as external URLs despite starting with "/" (RAISE-18837 R4).
  const nextIsSafe =
    (rawNext.startsWith("/") && !rawNext.startsWith("//") && !rawNext.startsWith("/\\")) ||
    (viewerOrigin !== "" && (rawNext === viewerOrigin || rawNext.startsWith(viewerOrigin + "/"))) ||
    rawNext === window.location.origin ||
    rawNext.startsWith(window.location.origin + "/") ||
    (viewerOrigin === "" && isLocalhostUrl(rawNext));
  const nextIsAbsolute = rawNext.startsWith("http://") || rawNext.startsWith("https://");
  const returnTo = (rawNext && nextIsSafe ? rawNext : null) ?? searchParams.get("returnTo") ?? "/";

  // Navigate to absolute viewer URL after login — must live in useEffect,
  // not in the render function (side effects in render are unreliable in Strict Mode).
  useEffect(() => {
    if (user && nextIsAbsolute && nextIsSafe) {
      window.location.href = returnTo;
    }
  }, [user, nextIsAbsolute, nextIsSafe, returnTo]);

  function doRedirect() {
    if (nextIsAbsolute && nextIsSafe) {
      window.location.href = returnTo;
    } else {
      navigate(returnTo, { replace: true });
    }
  }

  const flow = searchParams.get("flow");
  const [step, setStep] = useState<Step>(flow === "join" ? "email" : "password");
  const [loginPath, setLoginPath] = useState<LoginPath>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [resendHintVisible, setResendHintVisible] = useState(false);

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api<{ requires_org_selection?: boolean; orgs?: OrgOption[] }>(
        "/api/v2/auth/login",
        { method: "POST", body: JSON.stringify({ email, password }) },
      );
      if (res.requires_org_selection && res.orgs) {
        setOrgs(res.orgs);
        setLoginPath("password");
        setStep("org-select");
      } else {
        await refreshAuth();
        doRedirect();
      }
    } catch (err) {
      setError(
        mapApiError(
          err,
          { 401: t("login.invalidCredentials"), 403: t("login.emailNotVerified"), 429: t("login.tooManyAttempts") },
          t("login.genericError"),
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api("/api/v2/auth/magic-link", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setStep("code");
    } catch (err) {
      setError(mapApiError(err, { 429: t("login.tooManyAttempts") }, t("login.genericError")));
    } finally {
      setLoading(false);
    }
  }

  async function handleCodeSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api<{ requires_org_selection?: boolean; orgs?: OrgOption[] }>(
        "/api/v2/auth/verify",
        { method: "POST", body: JSON.stringify({ email, code }) },
      );
      if (res.requires_org_selection && res.orgs) {
        setOrgs(res.orgs);
        setLoginPath("magic-link");
        setStep("org-select");
      } else {
        await refreshAuth();
        doRedirect();
      }
    } catch (err) {
      setError(
        mapApiError(
          err,
          { 401: t("login.invalidCode"), 429: t("login.tooManyAttempts") },
          t("login.genericError"),
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleOrgSelect(orgId: string) {
    setError("");
    setLoading(true);
    try {
      if (loginPath === "password") {
        await api("/api/v2/auth/login", {
          method: "POST",
          body: JSON.stringify({ email, password, org_id: orgId }),
        });
      } else {
        await api("/api/v2/auth/verify", {
          method: "POST",
          body: JSON.stringify({ email, code, org_id: orgId }),
        });
      }
      await refreshAuth();
      doRedirect();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        if (loginPath === "password") {
          setError(t("login.invalidCredentials"));
          setStep("password");
        } else {
          setError(t("login.codeExpired"));
          setStep("email");
        }
      } else {
        setError(t("login.genericError"));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setError("");
    try {
      await api("/api/v2/auth/magic-link", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    } catch {
      setError(t("login.resendError"));
    }
    setResendHintVisible(true);
  }

  if (user) {
    if (nextIsAbsolute && nextIsSafe) {
      // useEffect above triggers window.location.href; render a link as fallback
      // so the user can click through if the auto-redirect takes a moment.
      return (
        <div className="flex min-h-screen items-center justify-center p-4 text-sm text-muted-foreground">
          <a href={returnTo} className="underline hover:text-foreground">
            Ir al documento
          </a>
        </div>
      );
    }
    return <Navigate to={returnTo} replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-1">
            <img src={logoUrl} alt="RaiSE" className="h-8 invert dark:invert-0" />
          </div>
          <CardDescription>
            {step === "password" && t("login.subtitlePassword")}
            {step === "email" && t("login.subtitleEmail")}
            {step === "code" && t("login.subtitleCode")}
            {step === "org-select" && t("login.subtitleOrgSelect")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === "password" && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("login.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t("login.password")}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" />
                    {t("login.submitting")}
                  </>
                ) : (
                  t("login.submit")
                )}
              </Button>
              <div className="flex flex-col gap-2 text-center text-sm">
                <Link to="/reset-password" className="text-muted-foreground hover:text-foreground">
                  {t("login.forgotPassword")}
                </Link>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setError("");
                    setStep("email");
                  }}
                >
                  {t("login.magicLinkCta")}
                </button>
              </div>
            </form>
          )}
          {step === "email" && (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("login.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" />
                    {t("login.sendingCode")}
                  </>
                ) : (
                  t("login.sendCode")
                )}
              </Button>
              <div className="text-center text-sm">
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setError("");
                    setStep("password");
                  }}
                >
                  {t("login.backToPassword")}
                </button>
              </div>
            </form>
          )}
          {step === "code" && (
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">{t("login.code")}</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" />
                    {t("login.verifying")}
                  </>
                ) : (
                  t("login.verify")
                )}
              </Button>
              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setStep("email");
                    setCode("");
                    setError("");
                    setResendHintVisible(false);
                  }}
                >
                  {t("login.back")}
                </button>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={handleResend}
                >
                  {t("login.resendCode")}
                </button>
              </div>
              {resendHintVisible && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  {t("login.resendHint")}
                </p>
              )}
            </form>
          )}
          {step === "org-select" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t("login.orgSelectPrompt")}
              </p>
              <div className="scrollbar-hide max-h-[50vh] space-y-2 overflow-y-auto">
                {orgs.map((org) => (
                  <Button
                    key={org.id}
                    variant="outline"
                    className="w-full justify-start"
                    disabled={loading}
                    onClick={() => handleOrgSelect(org.id)}
                  >
                    {org.name}
                  </Button>
                ))}
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
          )}
        </CardContent>
        <div className="pb-6 text-center text-sm text-muted-foreground">
          {t("login.noAccount")}{" "}
          <Link to="/entry" className="text-foreground underline hover:no-underline">
            {t("login.createOne")}
          </Link>
        </div>
      </Card>
    </div>
  );
}
